"use client"

import { useState } from "react"
import { useFormContext } from "react-hook-form"
import { HomeWithHomeOwnerFormType } from "../schema/home-with-home-owner.schema"
import { computeReliefStatus } from "../lib/relief-rebuilding-status"
import { Label } from "@/shared/components/ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { Info } from "lucide-react"
import { cn } from "@/shared/lib/utils"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { useActiveWorkScopes } from "../hooks/use-active-work-scopes"

const RELIEF_STATUS_HEADING = "Relief Status"

const RELIEF_STATUS_DESCRIPTIONS: Array<{ label?: string; text: string }> = [
  { text: "Reflects the current stage of disaster relief work on the home." },
  { text: "This status is automatically derived from relief work scopes such as Heavy Equipment, Tree, Exterior Debris, Roof Tarp, Crawl Space, Muck & Gut, and Mold." },
  { text: "It represents the status that best reflects overall progress across these relief work scopes." },
  { text: 'If all relief work scopes are blank, the status shows "Not Started."' },
  { text: 'If no relief work scopes are enabled for the region, the status shows "No Assistance Required."' },
  { text: 'If all relief steps are complete, the status shows "Complete."' },
  { text: 'If all relief steps are marked No Assistance Required or Homeowner Responsible, the status shows "No Assistance Required."' },
  { text: "Status options:" },
  { label: "Not Started", text: "Relief work has not yet begun." },
  { label: "In Progress", text: "One or more relief work scopes are currently underway." },
  { label: "Complete", text: "All identified relief work scopes have been finished." },
  { label: "No Assistance Required", text: "The home does not require relief assistance from any relief and/or rebuilding organization represented in ReliefHub or is outside the scope of those organizations. This may include situations where relief work is completed by the homeowner, another organization not represented in ReliefHub, or a contractor." },
  { label: "Unknown", text: "Relief recovery status cannot yet be determined." },
]

interface ReliefHomeStatusDisplayProps {
  className?: string
}

function ReliefHomeStatusDisplay({ className }: ReliefHomeStatusDisplayProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const formValues = useFormContext<HomeWithHomeOwnerFormType>().watch()
  const { session } = useSession()
  const { activeWorkScopes } = useActiveWorkScopes()
  const isVolunteer = session?.role === UserRole.VOLUNTEER
  const { displayLabel, colorClass } = computeReliefStatus(formValues, activeWorkScopes)

  return (
    <div className={cn("space-y-2 w-full", className)}>
      <div className="flex items-center gap-1.5">
        <Label className="form-label transition-colors duration-200">Relief Status</Label>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>
                  <button type="button" className="inline-flex text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded" aria-label="Relief Status explanation">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="start"
                sideOffset={8}
                className="w-[min(94vw,420px)] max-h-[40vh] md:max-h-[65vh] overflow-y-auto text-[11px] leading-tight"
              >
                <p className="font-semibold mb-1">{RELIEF_STATUS_HEADING}</p>
                <div className="space-y-1">
                  {RELIEF_STATUS_DESCRIPTIONS.map((item, idx) => (
                    <p key={`${item.label ?? "line"}-${idx}`}>
                      {item.label ? (
                        <>
                          <span className="font-semibold">{item.label}</span>
                          {" - "}
                          {item.text}
                        </>
                      ) : (
                        item.text
                      )}
                    </p>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <PopoverContent
            side="bottom"
            align="start"
            sideOffset={8}
            className="w-[min(94vw,440px)] max-h-[42vh] md:max-h-[72vh] overflow-y-auto text-xs leading-tight"
          >
            <p className="font-semibold mb-2">{RELIEF_STATUS_HEADING}</p>
            <div className="space-y-2">
              {RELIEF_STATUS_DESCRIPTIONS.map((item, idx) => (
                <p key={`${item.label ?? "line-pop"}-${idx}`}>
                  {item.label ? (
                    <>
                      <span className="font-semibold">{item.label}</span>
                      {" - "}
                      {item.text}
                    </>
                  ) : (
                    item.text
                  )}
                </p>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div
        className={cn(
          "flex h-9 w-full items-center rounded-md border px-3 py-2 text-sm shadow-sm transition-colors duration-200",
          colorClass
        )}
      >
        <span className={cn(isVolunteer && "opacity-50")}>{displayLabel}</span>
      </div>
    </div>
  )
}

export default ReliefHomeStatusDisplay
